import { ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import {cloneDeep} from 'lodash-es';
import { Observable } from 'rxjs';
import { debounceTime, map, skipWhile, startWith } from 'rxjs/operators';
import { ImoSearchModalService } from 'webcommon/provider-api';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlRequiredSearchParametersDto,
  FindControlUIBehaviorDto,
  IColumnPk,
} from 'webcommon/shared';
import { ApBaseComponent } from 'webcommon/core-ui';

interface AutocompleteGroup {
  AdditionalRows: boolean;
  isCollapsable: boolean;
  isCollapsed: boolean;
  GroupDisabled: boolean;
  Name: string;
  ItemList: IColumnPk[];
}

@Component({
  selector: 'ap-dxfindcontrol',
  templateUrl: './ap-dxfindcontrol.component.html',
})
export class ApDxFindControlComponent extends ApBaseComponent implements OnInit, OnChanges {
  @Input() baseId: string;
  @Input() findControlId: string;
  @Input() placeHolder: string;
  @Input() disabled: boolean;
  @Input() findControlDisplayName: string;
  @Input() requiredSearchParameters: FindControlRequiredSearchParametersDto;
  @Input() additionalSearchParameters: FindControlAdditionalSearchParametersDto;
  @Input() singleSelect = false;
  @Input() chips: IColumnPk[] = [];

  @Output() dirty = new EventEmitter<boolean>();

  @Input() selectedPks: string[] = [];
  @Output() selectedPksChange = new EventEmitter<string[]>();

  @Output() chipsChange = new EventEmitter<IColumnPk[]>();

  @ViewChild(MatAutocompleteTrigger, { static: true }) autocomplete: MatAutocompleteTrigger;

  Id: string;
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = false;
  returnedAutoCompleteOptions: Observable<IColumnPk[]>;
  formControl = new UntypedFormControl();
  findControlDto: FindControlUIBehaviorDto;

  defaultAutocompleteGroup: AutocompleteGroup;
  loadingConfig = false;  // loading find control config
  loadingItems = false;   // loading selected chip items
  loadingData = false;    // loading search results
  autoCompleteOpen = false;

  readonly separatorKeysCodes: number[] = [ENTER];
  readonly waitTime = 300;

  autocompleteGroups: Observable<AutocompleteGroup[]>;

  @ViewChild('chipInput', { static: true }) chipInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;

  constructor(private imoSearchModalService: ImoSearchModalService,
    ) {
    super();
  }

  ngOnInit() {
    this.load();
  }

  load() {
    this.defaultAutocompleteGroup = {
      AdditionalRows: false,
      GroupDisabled: true,
      ItemList: [],
      Name: 'Currently Selected',
      isCollapsable: true,
      isCollapsed: false,
    };
    this.setId();
    this.loadingData = false;
    this.searchSetup();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.baseId) {
      this.setId();
    }

    if (changes.disabled) {
      if (changes.disabled.currentValue) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    }
  }

  private searchSetup() {
    this.autocompleteGroups = this.formControl.valueChanges.pipe(
      startWith(null),
      debounceTime(this.waitTime),
      skipWhile(() => this.loadingConfig || this.loadingItems), // Don't load info while loading
      map(() => this.prependCurrentlySelected()),
    );
  }

  private prependCurrentlySelected(): AutocompleteGroup[] {
    const chipList = this.chips ? cloneDeep(this.chips) : [];
    this.defaultAutocompleteGroup.ItemList = chipList;
    return [this.defaultAutocompleteGroup];
  }

  openModal(event: Event) {
    event.stopPropagation();
    if (!this.disabled) {
      this.autocomplete.closePanel();
      const value = this.imoSearchModalService.openModalOnly(null, '', true);
      const chips = this.chips;

      value.result.then((results: any[]) => {
        results.forEach((result: any) => {
          try {
            chips.push({
              Code10: result.Icd10Codes[0],
              CodeDescription: result.PrimaryDiagnosis?.Description,
              LexicalCode: result.LexicalCode,
              Pk: '',
            });
          } catch (e) {
            console.error(e);
          }
        });

        this.updateSelectedPks();
      });
    }
  }

  getDisplayName(data: IColumnPk): string {
    return data.Code10;
  }

  getChipDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  getAutocompleteDisplayName(data: IColumnPk): string {
    return this.getDisplayName(data);
  }

  removeChip(object: IColumnPk): void {
    const index = this.chips.map((chip) => chip.LexicalCode).indexOf(object.LexicalCode);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.updateSelectedPks();
    }
  }

  onAutoCompleteOpen() {
    this.autoCompleteOpen = true;
  }

  onAutoCompleteClose() {
    this.autoCompleteOpen = false;
  }

  private updateSelectedPks() { // Call this after changing this.chips
    this.chips.sort((x, y) => {
      return x.Code10 > y.Code10 ? 1 : (x.Code10 < y.Code10 ? -1 : 0);
    });
    this.selectedPks = this.chips.map((selectedPk) => selectedPk.Pk);
    this.selectedPksChange.emit(this.selectedPks);
    this.chipsChange.emit(this.chips);
    this.formControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    // TODO only fire when changed
    this.dirty.emit(true);
  }

  private setId() {
    this.Id = `${this.baseId}-findcontrol`;
  }

  toggleCollapsed(event: Event, group: AutocompleteGroup) {
    event.stopPropagation();
    event.preventDefault();
    group.isCollapsed = !group.isCollapsed;
  }

  preventDefault(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  findControlClicked(event: Event) {
    // TODO look more into stopping event propagation, it's stopping the openEmitting from being called upstream
    // Ignore if control is disabled
    if (this.disabled) return;

    // Prevent default behavior and other events
    event.stopPropagation();
    event.preventDefault();

    // Focus the input box and open the autocomplete
    this.chipInput.nativeElement.focus();
    // Doesn't fire open event when focused through code so have to manually do it
    if (!this.autoCompleteOpen && !this.loadingConfig && !this.loadingItems) {
      this.onAutoCompleteOpen();
    }
  }
}
